<script setup lang="ts">
  import { onBeforeUnmount, onMounted, ref, watch } from 'vue';
  import { useRoute } from 'vue-router';

  const showOverlay = ref(false);
  const route = useRoute();

  const checkOrientation = () => {
    // Detect if the device supports touch (tablets, phones, iPads)
    const hasTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 1;

    // Detect portrait orientation
    const isPortrait = window.matchMedia('(orientation: portrait)').matches;

    // Treat any touch-capable device as mobile/tablet
    showOverlay.value = hasTouch && isPortrait;
  };

  const preventScroll = (e: Event) => e.preventDefault();

  // When overlay is visible, disable all scrolling & touch
  watch(showOverlay, val => {
    if (val) {
      document.body.style.overflow = 'hidden';
      document.documentElement.style.overflow = 'hidden';
      document.addEventListener('touchmove', preventScroll, { passive: false });
    } else {
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
      document.removeEventListener('touchmove', preventScroll);
    }
  });

  // Keep orientation in sync with route changes
  watch(
    () => route.fullPath,
    () => {
      // small timeout to allow new route to render before checking
      setTimeout(() => checkOrientation(), 100);
    }
  );

  onMounted(() => {
    checkOrientation();

    // Reliable matchMedia listener for orientation change
    const mql = window.matchMedia('(orientation: portrait)');

    mql.addEventListener('change', checkOrientation);

    // also listen for resize (fallback for browsers not supporting 'change')
    window.addEventListener('resize', checkOrientation);
  });

  onBeforeUnmount(() => {
    window.removeEventListener('resize', checkOrientation);
  });
</script>

<template>
  <Transition name="fade">
    <div v-if="showOverlay" class="orientation-lock-overlay d-flex flex-column align-center justify-center">
      <VCard elevation="12" width="340" color="surface" rounded="xl" class="pa-6 text-center">
        <div class="d-flex flex-column align-center justify-center">
          <VIcon icon="tabler-device-mobile-rotated" size="72" color="primary" />
          <h3 class="text-h6 mt-4">Rotate your device</h3>
          <p class="text-body-2 text-medium-emphasis mt-1">
            Please switch to
            <strong>landscape mode</strong>
            to continue.
          </p>
        </div>
      </VCard>
    </div>
  </Transition>
</template>

<style lang="scss" scoped>
  .orientation-lock-overlay {
    position: fixed;
    inset: 0;
    background: rgba(65, 65, 65, 0.9);
    backdrop-filter: blur(2px);
    z-index: 99999;
  }

  .fade-enter-active,
  .fade-leave-active {
    transition: opacity 0.25s ease;
  }
  .fade-enter-from,
  .fade-leave-to {
    opacity: 0;
  }

  @keyframes slow-rotate {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
  .rotate-icon {
    color: rgb(var(--v-theme-primary));
    animation: slow-rotate 4s linear infinite;
  }
</style>
